import Vue from 'vue';
import Component from 'vue-class-component';
import GlobalUtils from '@/lib/utils/global-utils';
import logger from '@/app/logger';

interface HTMLElement {
  requestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
  mozRequestFullscreen?: () => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void>;
  requestPictureInPicture?: () => Promise<void>;
}

@Component({
  name: 'player-controller-mixin'
})
export default class PlayerControllerMixin extends Vue {
  private buffering = false;
  private lastSpotLightItem: any = {};
  private animationInProgress = false;
  private requestAnimationFrame!: any;
  private cancelAnimationFrame!: any;
  private animatedFrame!: any;
  private relativeCta = {};
  showPlayButton = true;
  slideMode = false;
  frameId!: any;
  duration = 0;
  currentTime = 0;
  paintingInProgress = false;
  frameRate = 60;
  checkIntervalTimer: any = null;
  status = 'paused';
  volume = 0;
  muted = false;
  ratio = 16 / 9;
  inited = false;
  mode = 'default';
  pipInProgress = false;
  canvasVideo = document.createElement('video');
  hidePrev = true;
  hideNext = false;
  timeToEndSlide;
  showAllRelativeCtaLinks = false;
  caption = '';
  subtitleVisibility = false;
  hls: any;
  subtitleIndex = 0;
  hlsSubtitleTracks = {};
  video!: any;

  get source() {
    return this.video.accessUrl;
  }

  get spotLights() {
    return this.video.spotlights;
  }

  get relativeCtaLinks() {
    return this.video.relativeCtaLinks;
  }

  get socialLinks() {
    return this.video.socialLinks;
  }

  get stepsInfo() {
    return this.video.stepsInfo;
  }

  get quickJump() {
    return this.video.publishedVersion && this.video.publishedVersion.quickJump;
  }

  get canShowSlideMode() {
    return this.video.templateType == 'presentation';
  }

  get finalCta() {
    if (this.$isEmpty(this.relativeCtaLinks)) {
      return {};
    }
    return this.relativeCtaLinks.filter(l => l.data && l.data.endSlide)[0];
  }

  get uniqueRelativeCtaLinks() {
    if (this.$isEmpty(this.relativeCtaLinks)) {
      return [];
    }
    const labels = this.relativeCtaLinks.map(l => l.data.label);
    if (this.$isEmpty(labels)) {
      return [];
    }
    return this.relativeCtaLinks.filter(
      (link, index) => !labels.includes(link.data.label, index + 1)
    );
  }

  get initialSubtitleVisibility() {
    return (
      !this.$isMobile &&
      this.video &&
      this.video.createdAt &&
      this.video.createdAt > '2021-02-04T10:22:02Z'
    );
  }

  togglePlay() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    if (this.pipInProgress) {
      if (video.paused || video.ended) {
        this.togglePip(true);
      } else {
        this.canvasVideo.pause();
        this.canvasVideo.srcObject = null;
        video.requestPictureInPicture().then(() => {
          this.pipInProgress = true;
          video.pause();
        });
      }
    } else {
      if (video.paused || video.ended) {
        this.showPlayButton = false;
        if (this.slideMode) {
          this.replayIfEnded();
        } else {
          video.play();
        }
      } else {
        video.pause();
      }
    }
  }

  replayIfEnded() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    const startTime = GlobalUtils.binarySearch(
      video.currentTime,
      this.timeIntervals!
    );
    const index = this.timeIntervals!.indexOf(startTime!);
    let endTime = this.duration;
    if (index < this.timeIntervals!.length - 1) {
      endTime = this.timeIntervals![index + 1];
    }
    if (video.currentTime < endTime - 0.2) {
      video.play();
    } else {
      this.seek(startTime);
      video.play();
    }
  }

  playNext() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video.paused) {
      video.pause();
    }
    this.next();
    video.play();
  }

  playPrev() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video.paused) {
      video.pause();
    }
    this.prev();
    video.play();
  }

  exitSlideMode() {
    this.slideMode = false;
    this.timeToEndSlide = null;
  }

  enterSlideMode() {
    this.slideMode = true;
    this.timeToEndSlide = this.nextBlockEnd();
  }

  get timeIntervals() {
    if (this.$isEmpty(this.stepsInfo)) {
      return;
    }
    let timeIntervals = Object.values(this.stepsInfo) as number[];
    timeIntervals = timeIntervals.sort(
      (a1: number, b1: number) => a1 - b1
    ) as number[];
    return timeIntervals;
  }

  get currentSlide() {
    if (this.$isEmpty(this.stepsInfo)) {
      return;
    }
    const startTime = GlobalUtils.binarySearch(
      this.currentTime,
      this.timeIntervals!
    );
    return this.timeIntervals!.indexOf(startTime!) + 1;
  }

  nextBlockEnd() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    const startTime = GlobalUtils.binarySearch(
      video.currentTime,
      this.timeIntervals!
    );
    const index = this.timeIntervals!.indexOf(startTime!);
    this.hideNext = index == this.timeIntervals!.length - 1;
    this.hidePrev = index == 0;
    return index + 1 == this.timeIntervals!.length
      ? this.duration
      : this.timeIntervals![index + 1];
  }

  prev() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    const startTime = GlobalUtils.binarySearch(
      video.currentTime,
      this.timeIntervals!
    );
    const index = this.timeIntervals!.indexOf(startTime!);
    this.hidePrev = index - 1 == 0;
    this.hideNext = index - 1 == this.timeIntervals!.length - 1;
    if (index > 0) {
      if (this.slideMode) {
        video.currentTime = this.timeIntervals![index - 1] + 0.6;
      } else {
        video.currentTime = this.timeIntervals![index - 1];
      }
      this.timeToEndSlide = startTime;
    }
  }

  next() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    const startTime = GlobalUtils.binarySearch(
      video.currentTime,
      this.timeIntervals!
    );
    const index = this.timeIntervals!.indexOf(startTime!);
    this.hideNext = index + 1 == this.timeIntervals!.length - 1;
    this.hidePrev = index + 1 == 0;
    if (index < this.timeIntervals!.length - 1) {
      video.currentTime = this.timeIntervals![index + 1];
      this.timeToEndSlide =
        index + 2 == this.timeIntervals!.length
          ? this.duration
          : this.timeIntervals![index + 2];
    }
  }

  play(event) {
    if (!event.target) {
      return;
    }
    this.checkIntervalTimer = window.setInterval(() => {
      this.drawCanvas();
      if (!this.$isEmpty(this.hlsSubtitleTracks)) {
        this.displaySubtitles();
      }
      if (!this.$isEmpty(this.relativeCtaLinks)) {
        this.displayRelativeLinks();
      }
    }, 1000 / this.frameRate);
    this.status = 'playing';
  }

  pause(event) {
    if (!event.target) {
      return;
    }
    window.clearInterval(this.checkIntervalTimer);
    if (event.target.paused) {
      this.status = 'paused';
    }
  }

  ended(event) {
    if (!event.target) {
      return;
    }
    if (event.target.ended) {
      this.status = 'ended';
      this.$emit('ended');
    }
  }

  seek(seekTime) {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    this.clearAnimation();
    this.lastSpotLightItem = {};
    this.$nextTick(() => {
      video.currentTime = seekTime;
    });
  }

  seeking() {
    this.drawCanvas();
  }

  timeupdate(event) {
    if (!event.target) {
      return;
    }
    this.currentTime = event.target.currentTime;
    if (this.slideMode && this.timeToEndSlide) {
      const video = this.$refs.videoElement as HTMLVideoElement;
      if (video.currentTime + 0.2 >= this.timeToEndSlide) {
        if (video.currentTime >= this.timeToEndSlide) {
          video.currentTime = this.timeToEndSlide - 0.2;
        }
        video.pause();
      }
    }
  }

  volumechange(value) {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    if (video.muted) {
      video.muted = false;
    }
    video.volume = value;
  }

  mute() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    video.muted = true;
  }

  unmute() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    video.muted = false;
  }

  forward() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (video && video.currentTime >= this.duration) {
      return;
    }
    if (video.currentTime + 5 < this.duration) {
      video.currentTime = video.currentTime + 5;
      return;
    }
    video.currentTime = this.duration;
    // if (this.$isEmpty(this.spotLights)) {
    //   return;
    // }
    // if (this.$isEmpty(this.lastSpotLightItem)) {
    //   const first = this.spotLights[0];
    //   first && this.seek(first.startTime);
    // } else {
    //   const lastindex = this.spotLights.indexOf(this.lastSpotLightItem);
    //   if (lastindex == this.spotLights.length - 1) {
    //     this.seek(this.duration);
    //     return;
    //   }
    //   this.spotLights[lastindex + 1] &&
    //     this.seek(this.spotLights[lastindex + 1].startTime);
    // }
  }

  backward() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (video && video.currentTime <= 0) {
      return;
    }
    if (video.currentTime - 5 > 0) {
      video.currentTime = video.currentTime - 5;
      return;
    }
    video.currentTime = 0;
    // if (this.$isEmpty(this.spotLights)) {
    //   return;
    // }
    // if (this.$isEmpty(this.lastSpotLightItem)) {
    //   this.seek(0);
    // } else {
    //   const lastindex = this.spotLights.indexOf(this.lastSpotLightItem);
    //   if (lastindex == 0) {
    //     this.seek(0);
    //     return;
    //   }
    //   this.spotLights[lastindex - 1] &&
    //     this.seek(this.spotLights[lastindex - 1].startTime);
    // }
  }

  onvolumechange(event) {
    if (!event.target) {
      return;
    }
    this.muted = event.target.muted;
  }

  waiting(event) {
    this.buffering = true;
  }

  canplay(event) {
    this.buffering = false;
  }

  subtitle(value) {
    this.subtitleVisibility = value;
  }

  playbackRate(value) {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    video.playbackRate = value;
  }

  displaySubtitles() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    const currentTime = video.currentTime;
    const item =
      this.hlsSubtitleTracks &&
      this.hlsSubtitleTracks[`subtitles${this.subtitleIndex}`] &&
      this.hlsSubtitleTracks[`subtitles${this.subtitleIndex}`].filter(
        t =>
          t.startTime == currentTime ||
          Number(t.startTime.toFixed(2)) == Number(currentTime.toFixed(2)) ||
          this.$between(currentTime, t.startTime, t.endTime)
      )[0];
    if (this.$isEmpty(item)) {
      this.caption = '';
      return;
    }
    this.caption = item.text;
  }

  displayRelativeLinks() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    const currentTime = video.currentTime;
    const item =
      this.relativeCtaLinks &&
      this.relativeCtaLinks.filter(
        i =>
          !(i.data && i.data.endSlide) &&
          (i.startTime == currentTime ||
            Number(i.startTime.toFixed(2)) == Number(currentTime.toFixed(2)) ||
            this.$between(currentTime, i.startTime, i.startTime + i.duration))
      )[0];
    if (this.$isEmpty(item)) {
      this.relativeCta = {};
      return;
    }
    this.relativeCta = item.data;
  }

  toggleRelativeLinks() {
    this.showAllRelativeCtaLinks = !this.showAllRelativeCtaLinks;
  }

  setSubtitle(subtitle) {
    if (this.$isEmpty(subtitle)) {
      this.subtitleVisibility = false;
    }
    if (!this.hls || this.$isEmpty(this.hls.subtitleTracks)) {
      return;
    }
    const index = this.hls.subtitleTracks.indexOf(subtitle);
    if (index != -1) {
      this.hls.subtitleTrack = index;
      this.subtitleVisibility = true;
    }
  }

  drawCanvas() {
    if (this.$isEmpty(this.spotLights) || this.animationInProgress) {
      return;
    }
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    const currentTime = video.currentTime;
    const item =
      this.spotLights &&
      this.spotLights.filter(
        i =>
          i.startTime == currentTime ||
          Number(i.startTime.toFixed(2)) == Number(currentTime.toFixed(2)) ||
          this.$between(currentTime, i.startTime, i.startTime + i.duration)
      )[0];
    const canvas = this.$refs['player-canvas'] as HTMLCanvasElement;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (this.pipInProgress) {
      this.drawVideo(canvas, ctx, video);
    }
    if (!this.$isEmpty(item)) {
      if (
        !this.$isEmpty(this.lastSpotLightItem) &&
        this.lastSpotLightItem != item &&
        this.animatedFrame != item.id &&
        this.canAnimate(this.lastSpotLightItem, item)
      ) {
        this.startElementAnimation(canvas, ctx, this.lastSpotLightItem, item);
        this.lastSpotLightItem = item;
      } else {
        this.clearAnimation();
        this.lastSpotLightItem = item;
        if (this.$isEmpty(item.ordinates)) {
          return;
        }
        this.drawOrdinates(canvas, ctx, item);
        this.paintingInProgress = true;
      }
    } else {
      if (!this.pipInProgress && !this.$isEmpty(this.lastSpotLightItem)) {
        const index = this.spotLights.indexOf(this.lastSpotLightItem);
        const forward = this.currentTime > this.lastSpotLightItem.startTime;
        if (forward) {
          if (index == this.spotLights.length - 1) {
            this.clearAnimation();
            this.lastSpotLightItem = {};
            this.animatedFrame = undefined;
            return;
          }
          const nextItem = this.spotLights[index + 1];
          if (!this.canAnimate(this.lastSpotLightItem, nextItem)) {
            this.clearAnimation();
          } else {
            if (this.animatedFrame == (nextItem && nextItem.id)) {
              return;
            }
            this.startElementAnimation(
              canvas,
              ctx,
              this.lastSpotLightItem,
              nextItem
            );
          }
        } else {
          if (index == 0) {
            this.clearAnimation();
            this.lastSpotLightItem = {};
            this.animatedFrame = undefined;
            return;
          }
          const prevItem = this.spotLights[index - 1];
          if (!this.canAnimate(this.lastSpotLightItem, prevItem)) {
            this.clearAnimation();
          } else {
            if (this.animatedFrame == (prevItem && prevItem.id)) {
              return;
            }
            this.startElementAnimation(
              canvas,
              ctx,
              this.lastSpotLightItem,
              prevItem
            );
          }
        }
      } else {
        this.clearAnimation();
      }
    }
  }

  canAnimate(prev, next) {
    const duration = next.startTime - (prev.startTime + prev.duration);
    const prevRect = this.rects(prev);
    const nextRect = this.rects(next);
    if (!prevRect || !nextRect) {
      return;
    }
    const displacement = {
      x: Math.abs(nextRect.x - prevRect.x),
      y: Math.abs(nextRect.y - prevRect.y)
    };
    const maxAllowedDuration = 2.5;
    const maxAllowedDisplacement = 200;
    return (
      !this.pipInProgress &&
      duration <= maxAllowedDuration &&
      next.ordinates &&
      next.ordinates.animate &&
      (displacement.x < maxAllowedDisplacement ||
        displacement.y < maxAllowedDisplacement)
    );
  }

  clearAnimation() {
    this.paintingInProgress = false;
    this.animationInProgress = false;
    window.cancelAnimationFrame(this.frameId);
  }

  mounted() {
    this.subtitleVisibility = this.initialSubtitleVisibility;
    const vendors = ['ms', 'moz', 'webkit', 'o'];
    for (let x = 0; x < vendors.length && !window.requestAnimationFrame; ++x) {
      window.requestAnimationFrame =
        window[vendors[x] + 'RequestAnimationFrame'];
      window.cancelAnimationFrame =
        window[vendors[x] + 'CancelAnimationFrame'] ||
        window[vendors[x] + 'CancelRequestAnimationFrame'];
    }

    let lastTime = 0;
    if (!window.requestAnimationFrame) {
      window.requestAnimationFrame = function(callback) {
        const currTime = new Date().getTime();
        const timeToCall = Math.max(0, 16 - (currTime - lastTime));
        const id = window.setTimeout(function() {
          callback(currTime + timeToCall);
        }, timeToCall);
        lastTime = currTime + timeToCall;
        return id;
      };
    }

    if (!window.cancelAnimationFrame)
      window.cancelAnimationFrame = function(id) {
        clearTimeout(id);
      };
  }

  animate(canvas, ctx, start, end, duration, item) {
    const min = 1;
    const max = 2;
    duration = duration > max ? max : duration < min ? min : duration;
    const displacement = {
      x: end.x - start.x,
      y: end.y - start.y,
      w: end.w - start.w,
      h: end.h - start.h
    };

    if (Math.abs(displacement.x) > 0) {
      start.x = start.x + displacement.x / (duration * 10);
    }
    if (Math.abs(displacement.y) > 0) {
      start.y = start.y + displacement.y / (duration * 10);
    }
    if (Math.abs(displacement.w) > 0) {
      start.w = start.w + displacement.w / (duration * 10);
    }
    if (Math.abs(displacement.h) > 0) {
      start.h = start.h + displacement.h / (duration * 10);
    }

    if (
      Math.abs(Math.floor(displacement.x)) > 1 ||
      Math.abs(Math.floor(displacement.y)) > 1 ||
      Math.abs(Math.floor(displacement.w)) > 1 ||
      Math.abs(Math.floor(displacement.h)) > 1
    ) {
      this.clipRect(canvas, ctx, start.x, start.y, start.w, start.h);
      this.frameId = window.requestAnimationFrame(() => {
        this.animate(canvas, ctx, start, end, duration, item);
      });
    } else {
      this.animatedFrame = item.id;
      this.animationInProgress = false;
      window.cancelAnimationFrame(this.frameId);
      return;
    }
  }

  rects(item) {
    const canvas = this.$refs['player-canvas'] as HTMLCanvasElement;
    if (!canvas || !item.ordinates) {
      return;
    }
    const scaleFactor = window.devicePixelRatio || 1;
    const rendererXScaling =
      (canvas.clientWidth / item.ordinates.width) * scaleFactor;
    const rendererYScaling =
      (canvas.clientHeight / item.ordinates.height) * scaleFactor;
    return {
      x: item.ordinates.x * rendererXScaling,
      y: item.ordinates.y * rendererYScaling,
      w: item.ordinates.rectWidth * rendererXScaling,
      h: item.ordinates.rectHeight * rendererYScaling
    };
  }

  startElementAnimation(canvas, ctx, prevItem, nextItem) {
    if (!canvas || !ctx || this.$isEmpty(prevItem) || this.$isEmpty(nextItem)) {
      this.animationInProgress = false;
      this.paintingInProgress = false;
      return;
    }
    this.animationInProgress = true;
    this.paintingInProgress = true;
    const prev = this.rects(prevItem);
    if (!prev) {
      return;
    }
    this.clipRect(canvas, ctx, prev.x, prev.y, prev.w, prev.h);
    const next = this.rects(nextItem);
    this.animate(
      canvas,
      ctx,
      prev,
      next,
      nextItem.startTime - (prevItem.startTime + prevItem.duration),
      nextItem
    );
  }

  drawVideo(canvas, ctx, video) {
    ctx.drawImage(
      video,
      0,
      0,
      video.videoWidth,
      video.videoHeight,
      0,
      0,
      canvas.clientWidth * window.devicePixelRatio,
      canvas.clientHeight * window.devicePixelRatio
    );
  }

  roundRect(ctx, x, y, width, height, radius) {
    if (typeof radius === 'undefined') {
      radius = 4;
    }
    if (typeof radius === 'number') {
      radius = {
        tl: radius,
        tr: radius,
        br: radius,
        bl: radius
      };
    } else {
      const defaultRadius = {
        tl: 0,
        tr: 0,
        br: 0,
        bl: 0
      };
      for (const side in defaultRadius) {
        radius[side] = radius[side] || defaultRadius[side];
      }
    }
    ctx.beginPath();
    ctx.moveTo(x + radius.tl, y);
    ctx.lineTo(x + width - radius.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    ctx.lineTo(x + width, y + height - radius.br);
    ctx.quadraticCurveTo(
      x + width,
      y + height,
      x + width - radius.br,
      y + height
    );
    ctx.lineTo(x + radius.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    ctx.lineTo(x, y + radius.tl);
    ctx.quadraticCurveTo(x, y, x + radius.tl, y);
    ctx.closePath();
  }

  clipRect(canvas, ctx, x, y, w, h) {
    const scaleFactor = window.devicePixelRatio || 1;
    ctx.clearRect(
      0,
      0,
      canvas.clientWidth * scaleFactor,
      canvas.clientHeight * scaleFactor
    );
    // draw rounded radius
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.rect(
      0,
      0,
      canvas.clientWidth * scaleFactor,
      canvas.clientHeight * scaleFactor
    );
    ctx.fill();
    this.drawFocusLines(ctx, x, y, w, h);
    ctx.save();
    this.roundRect(ctx, x, y, w, h, 2);
    ctx.clip();
    ctx.clearRect(
      0,
      0,
      canvas.clientWidth * scaleFactor,
      canvas.clientHeight * scaleFactor
    );
    ctx.restore();
  }

  drawOrdinates(canvas, ctx, spotlight) {
    if (!canvas || !ctx || !spotlight.ordinates) {
      return;
    }
    const scaleFactor = window.devicePixelRatio || 1;
    const rendererXScaling =
      (canvas.clientWidth / spotlight.ordinates.width) * scaleFactor;
    const rendererYScaling =
      (canvas.clientHeight / spotlight.ordinates.height) * scaleFactor;

    const [x, y, w, h] = [
      spotlight.ordinates.x * rendererXScaling,
      spotlight.ordinates.y * rendererYScaling,
      spotlight.ordinates.rectWidth * rendererXScaling,
      spotlight.ordinates.rectHeight * rendererYScaling
    ];
    if (!this.pipInProgress) {
      this.clipRect(canvas, ctx, x, y, w, h);
    } else {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.beginPath();
      ctx.rect(0, 0, x, y);
      ctx.rect(0, 0, x, canvas.clientHeight * scaleFactor);
      ctx.rect(x, 0, canvas.clientWidth * scaleFactor, y);
      ctx.rect(x, y + h, x + w, canvas.clientHeight * scaleFactor);
      ctx.rect(
        x + w,
        y,
        canvas.clientWidth * scaleFactor,
        canvas.clientHeight * scaleFactor
      );
      ctx.closePath();
      ctx.fill();
      this.drawFocusLines(ctx, x, y, w, h);
    }
  }

  drawFocusLines(ctx, x, y, w, h) {
    const scaleFactor = window.devicePixelRatio || 1;
    const lineWidth = 8 * scaleFactor;
    ctx.strokeStyle = 'white';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.5 * scaleFactor;
    const lineLength = 6 * scaleFactor;
    const spacing = lineWidth / (4 / 3);
    // top-left
    ctx.beginPath();
    ctx.moveTo(x - spacing, y - spacing);
    ctx.lineTo(x - spacing, y - spacing + lineLength);
    ctx.moveTo(x - spacing, y - spacing);
    ctx.lineTo(x - spacing + lineLength, y - spacing);
    ctx.closePath();
    ctx.stroke();
    // top-right
    ctx.beginPath();
    ctx.moveTo(x + w + spacing, y - spacing);
    ctx.lineTo(x + w + spacing, y - spacing + lineLength);
    ctx.moveTo(x + w + spacing, y - spacing);
    ctx.lineTo(x + w + spacing - lineLength, y - spacing);
    ctx.closePath();
    ctx.stroke();
    // bottom-left
    ctx.beginPath();
    ctx.moveTo(x - spacing, y + h + spacing);
    ctx.lineTo(x - spacing, y + h + spacing - lineLength);
    ctx.moveTo(x - spacing, y + h + spacing);
    ctx.lineTo(x - spacing + lineLength, y + h + spacing);
    ctx.closePath();
    ctx.stroke();
    // bottom-right
    ctx.beginPath();
    ctx.moveTo(x + w + spacing, y + h + spacing);
    ctx.lineTo(x + w + spacing, y + h + spacing - lineLength);
    ctx.moveTo(x + w + spacing, y + h + spacing);
    ctx.lineTo(x + w + spacing - lineLength, y + h + spacing);
    ctx.closePath();
    ctx.stroke();
  }

  fullscreen() {
    const container = this.$refs['frame'] as HTMLElement;
    if (!document.fullscreenElement) {
      document.addEventListener('fullscreenchange', this.fullscreenchange);
      document.addEventListener(
        'webkitfullscreenchange',
        this.fullscreenchange
      );

      if (container.requestFullscreen) {
        try {
          container.requestFullscreen();
        } catch (e) {
          logger.error(e);
        }
      } else if (container.mozRequestFullscreen) {
        // Firefox
        container.mozRequestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        // Chrome, Safari & Opera
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        // edge
        container.msRequestFullscreen();
      }
    }
  }

  defaultscreen() {
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement
    ) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }

  fullscreenchange() {
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement
    ) {
      this.mode = 'fullscreen';
    } else {
      this.mode = 'defaultview';
    }
    this.inited = false;
    window.setTimeout(() => {
      this.resetCanvasProperties();
    }, 100);
  }

  async togglePip(skipCheck = false) {
    const video = this.$refs.videoElement as HTMLVideoElement;
    if (!video) {
      return;
    }
    try {
      if (skipCheck || video !== document['pictureInPictureElement']) {
        if (video.paused) {
          await video.play();
        }
        const canvas = this.$refs['player-canvas'] as HTMLCanvasElement;
        this.canvasVideo.srcObject = canvas.captureStream();
        this.canvasVideo.muted = true;
        this.pipInProgress = true;
        this.canvasVideo.addEventListener('leavepictureinpicture', () => {
          // Video left Picture-in-Picture mode.
          this.pipInProgress = false;
        });
        await this.canvasVideo.play();
        await this.canvasVideo.requestPictureInPicture();
      } else {
        this.pipInProgress = false;
        await document.exitPictureInPicture();
      }
    } catch (error) {
      console.error(error);
    } finally {
    }
  }

  setCanvasProperties() {
    const video = this.$refs.videoElement as HTMLVideoElement;
    const canvas = this.$refs['player-canvas'] as HTMLCanvasElement;
    if (!video || !canvas || !video.videoHeight || !video.videoWidth) {
      return;
    }

    let height = video.clientHeight;
    let width = video.clientWidth;

    // if (video.videoWidth / video.videoHeight != 16 / 9) {
    const ratio = video.videoWidth / video.videoHeight;
    if (width / ratio > height) {
      width = ratio * height;
    } else {
      height = width / ratio;
    }
    // }

    canvas.style.height = height + 'px';
    canvas.style.width = width + 'px';
    const scaleFactor = window.devicePixelRatio;
    canvas.setAttribute('width', width * scaleFactor + '');
    canvas.setAttribute('height', height * scaleFactor + '');
  }

  resetCanvasProperties() {
    this.paintingInProgress = false;
    this.setCanvasProperties();
  }
}
